import AddIcon from '@mui/icons-material/Add';
import { Alert, Box } from '@mui/material';
import { Button, SearchBar } from '@peritasai/ui-components';
import type { PreferenceCardListItemEntity } from '@peritasai/repository';
import { usePreferenceCards } from '@peritasai/repository';
import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import type { GridPaginationModel, GridSortModel } from '@mui/x-data-grid-premium';
import { PreferenceCardDrawer } from './PreferenceCardDrawer';
import { DrawerMode } from '../../utils/drawer';
import { PreferenceCardsTable } from './PreferenceCardsTable';

export const PreferenceCardsPage = () => {
  const { t } = useTranslation(['periverse']);
  const [searchInput, setSearchInput] = useState('');
  const [activeSearch, setActiveSearch] = useState('');
  const [paginationModel, setPaginationModel] = useState<GridPaginationModel>({
    page: 0,
    pageSize: 10,
  });
  const [sortModel, setSortModel] = useState<GridSortModel>([]);
  const [drawerOpen, setDrawerOpen] = useState(false);
  const [drawerMode, setDrawerMode] = useState<DrawerMode>(DrawerMode.Create);
  const [selectedItem, setSelectedItem] = useState<PreferenceCardListItemEntity | null>(null);

  const activeSortItem = sortModel[0];
  const { data, isLoading, isError } = usePreferenceCards({
    search: activeSearch || undefined,
    page: paginationModel.page,
    limit: paginationModel.pageSize,
    sortBy: activeSortItem?.field,
    sortOrder: activeSortItem ? (activeSortItem.sort as 'asc' | 'desc') : undefined,
    isActive: true,
  });

  const rows = data?.preferenceCards ?? [];
  const rowCount = data?.total ?? 0;

  const handleInputChange = (value: string) => {
    setSearchInput(value);
    if (!value) {
      setActiveSearch('');
      setPaginationModel((prev) => ({ ...prev, page: 0 }));
    }
  };

  const handleSearch = (value: string) => {
    setActiveSearch(value);
    setPaginationModel((prev) => ({ ...prev, page: 0 }));
  };

  const handleClear = () => {
    setSearchInput('');
    setActiveSearch('');
    setPaginationModel((prev) => ({ ...prev, page: 0 }));
  };

  const handleSortModelChange = (model: GridSortModel) => {
    setSortModel(model);
    setPaginationModel((prev) => ({ ...prev, page: 0 }));
  };

  const handleRowClick = (row: PreferenceCardListItemEntity) => {
    setSelectedItem(row);
    setDrawerMode(DrawerMode.Edit);
    setDrawerOpen(true);
  };

  const handleAddClick = () => {
    setSelectedItem(null);
    setDrawerMode(DrawerMode.Create);
    setDrawerOpen(true);
  };

  const handleDrawerClose = () => {
    setDrawerOpen(false);
  };

  return (
    <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, justifyContent: 'space-between' }}>
        <SearchBar
          value={searchInput}
          onChange={handleInputChange}
          onSearch={handleSearch}
          onClear={handleClear}
          placeholder={`${t('pages.entityManagement.searchPlaceholder', { entityName: t('pages.entityManagement.items.preferenceCards') })}...`}
          width={{ xs: '100%', sm: '45%' }}
        />
        <Button
          variant="contained"
          startIcon={<AddIcon />}
          onClick={handleAddClick}
          sx={{ flexShrink: 0 }}
        >
          {t('common.add')}
        </Button>
      </Box>

      {isError && (
        <Alert severity="error">
          {t('pages.entityManagement.loadError')}
        </Alert>
      )}

      <PreferenceCardsTable
        rows={rows}
        rowCount={rowCount}
        isLoading={isLoading}
        hasSearch={activeSearch.length > 0}
        paginationModel={paginationModel}
        onPaginationModelChange={setPaginationModel}
        onRowClick={handleRowClick}
        sortModel={sortModel}
        onSortModelChange={handleSortModelChange}
      />

      <PreferenceCardDrawer
        open={drawerOpen}
        mode={drawerMode}
        selectedItem={selectedItem}
        onClose={handleDrawerClose}
      />
    </Box>
  );
};
